import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { formatCurrency } from '../lib/utils';
import {
  CheckCircle2, XCircle, AlertTriangle, TrendingUp, TrendingDown,
  Clock, Shield, Wallet, PiggyBank, Scale, Target, Lightbulb,
  ArrowRight, ChevronDown, ChevronUp, BarChart3, Percent, Infinity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Traffic light indicator
const Signal = ({ status }) => {
  const config = {
    green: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/30' },
    yellow: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/30' },
    red: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30' },
  };
  const c = config[status] || config.yellow;
  return (
    <div className={`w-10 h-10 rounded-full ${c.bg} border ${c.border} flex items-center justify-center`}>
      <c.icon className={`w-5 h-5 ${c.color}`} />
    </div>
  );
};

// Collapsible section
const Section = ({ icon: Icon, title, subtitle, signal, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="overflow-hidden" data-testid={`section-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-5 hover:bg-secondary/30 transition-colors text-left"
      >
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground truncate">{subtitle}</p>}
        </div>
        {signal && <Signal status={signal} />}
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && <CardContent className="pt-0 pb-5 px-5 border-t border-border/30">{children}</CardContent>}
    </Card>
  );
};

// Simple metric row
const Metric = ({ label, value, hint }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <span className="text-sm text-muted-foreground">{label}</span>
      {hint && <p className="text-xs text-muted-foreground/70">{hint}</p>}
    </div>
    <span className="font-semibold tabular-nums">{value}</span>
  </div>
);

export const ProjectExplainedPage = () => {
  const { projectId } = useParams();
  const { currentProject, fetchProject, getCalculation, getDashboard } = useProject();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (!currentProject || currentProject.id !== projectId) {
          await fetchProject(projectId);
        }
        const [dashboard, breakeven, scenarios, valuationData] = await Promise.all([
          getDashboard(projectId),
          getCalculation(projectId, 'breakeven'),
          getCalculation(projectId, 'scenarios'),
          getCalculation(projectId, 'valuation'),
        ]);
        setData({ dashboard, breakeven, scenarios, valuationData });
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  if (loading || !data?.dashboard) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading">
        <div className="animate-pulse text-muted-foreground">Analizando tu proyecto...</div>
      </div>
    );
  }

  const project = data.dashboard.project;
  const val = data.dashboard.valuation;
  const income = data.dashboard.income_statement;
  const cf = data.dashboard.cash_flow;
  const be = data.breakeven;
  const scenarios = data.scenarios?.scenarios || {};

  // Perpetuity data
  const perpData = data.valuationData?.perpetuity || {};
  const perpEnabled = perpData.enabled;
  const terminalValue = perpData.terminal_value || 0;
  const perpFormula = perpData.formula_breakdown;

  const currency = project?.specifications?.currency || 'MXN';
  const fc = (v) => formatCurrency(v, currency);
  const years = project?.specifications?.project_life_years || 5;
  const fin = project?.financing || {};
  const totalInv = fin.total_investment || 0;
  const equityAmt = fin.equity_amount || 0;
  const debtAmt = fin.debt_amount || 0;
  const debtPct = fin.debt_percentage || 0;
  const npv = val.npv || 0;
  const irr = val.project_irr || val.irr || 0;
  const wacc = val.wacc || 0;
  const payback = val.payback_period;
  const decision = val.decision;
  const isViable = decision === 'ACEPTADO';

  // Totals from income statement
  const totalRevenue = income.years.reduce((s, y) => s + y.revenue, 0);
  const totalNetIncome = income.years.reduce((s, y) => s + y.net_income, 0);
  const avgNetIncomeMonthly = totalNetIncome / (years * 12);
  const year1Revenue = income.years[0]?.revenue || 0;
  const lastYearRevenue = income.years[income.years.length - 1]?.revenue || 0;
  const revenueGrowth = year1Revenue > 0 ? ((lastYearRevenue / year1Revenue - 1) * 100) : 0;

  // Breakeven
  const marginSafety = be?.margin_of_safety || 0;
  const beUnits = be?.breakeven_units || 0;
  const projectedUnits = be?.projected_annual_units || 0;

  // Scenario info
  const pessimistic = scenarios.pessimistic || {};
  const pessDecision = pessimistic.decision;

  // Signal logic
  const investSignal = totalInv > 0 ? 'green' : 'yellow';
  const finSignal = debtPct <= 50 ? 'green' : debtPct <= 70 ? 'yellow' : 'red';
  const profitSignal = totalNetIncome > 0 ? 'green' : 'red';
  const paybackSignal = payback ? (payback <= years * 0.4 ? 'green' : payback <= years * 0.7 ? 'yellow' : 'red') : 'red';
  const viabilitySignal = isViable ? (irr > wacc * 2 ? 'green' : 'green') : 'red';
  const beSignal = marginSafety > 30 ? 'green' : marginSafety > 10 ? 'yellow' : 'red';
  const scenarioSignal = pessDecision === 'ACEPTADO' ? 'green' : 'yellow';

  return (
    <div className="space-y-6 max-w-3xl mx-auto animate-fade-in" data-testid="project-explained-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">PASO 12 DE 12</span>
        </div>
        <h1 className="text-4xl font-bold">Tu Proyecto Explicado</h1>
        <p className="text-muted-foreground mt-1">
          Todo lo que necesitas saber, sin jerga financiera.
        </p>
      </div>

      {/* Verdict */}
      <Card className={isViable
        ? 'border-emerald-400/40 bg-emerald-400/5'
        : 'border-red-400/40 bg-red-400/5'
      }>
        <CardContent className="p-6 flex items-center gap-5" data-testid="verdict-card">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${isViable ? 'bg-emerald-400/20' : 'bg-red-400/20'}`}>
            {isViable
              ? <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              : <XCircle className="w-8 h-8 text-red-400" />
            }
          </div>
          <div>
            <h2 className="text-xl font-bold">
              {isViable
                ? 'Tu proyecto se ve bien. Los numeros dicen que vale la pena.'
                : 'Los numeros sugieren que necesitas ajustar algunas cosas.'
              }
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {isViable
                ? `Basado en ${years} años de proyeccion, tu negocio generaria mas dinero del que cuesta ponerlo en marcha.`
                : `Con los datos actuales, la inversion no se recuperaria satisfactoriamente en ${years} años. Revisa los puntos marcados en rojo.`
              }
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Section 1: Investment */}
      <Section icon={Wallet} title="¿Cuanto necesitas para empezar?" subtitle={fc(totalInv)} signal={investSignal}>
        <div className="space-y-3 mt-3">
          <p className="text-sm text-muted-foreground">
            Para poner en marcha tu proyecto necesitas <strong className="text-foreground">{fc(totalInv)}</strong>. Esto incluye todo lo que debes comprar, rentar o preparar antes de abrir.
          </p>
          {project.investments?.length > 0 && (
            <div className="space-y-1">
              {project.investments.slice(0, 5).map((inv, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg bg-secondary/30">
                  <span>{inv.concept}</span>
                  <span className="font-semibold tabular-nums">{fc(inv.amount)}</span>
                </div>
              ))}
              {project.investments.length > 5 && (
                <p className="text-xs text-muted-foreground px-3">...y {project.investments.length - 5} conceptos mas</p>
              )}
            </div>
          )}
          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-xs text-primary flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5" />
              Consejo: Cotiza con al menos 3 proveedores cada concepto para asegurar que tus cifras sean realistas.
            </p>
          </div>
        </div>
      </Section>

      {/* Section 2: Financing */}
      <Section icon={PiggyBank} title="¿Como vas a financiarlo?" subtitle={debtPct > 0 ? `${(100 - debtPct).toFixed(0)}% tuyo, ${debtPct.toFixed(0)}% prestado` : '100% con tu capital'} signal={finSignal}>
        <div className="space-y-3 mt-3">
          {debtPct > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">
                Pondras <strong className="text-foreground">{fc(equityAmt)}</strong> de tu bolsillo y pediras prestado <strong className="text-foreground">{fc(debtAmt)}</strong>.
              </p>
              {/* Visual bar */}
              <div className="h-5 rounded-full overflow-hidden flex bg-secondary/50">
                <div className="bg-primary h-full flex items-center justify-center text-[10px] font-bold text-primary-foreground" style={{ width: `${100 - debtPct}%` }}>
                  {(100 - debtPct).toFixed(0)}%
                </div>
                <div className="bg-amber-500 h-full flex items-center justify-center text-[10px] font-bold text-white" style={{ width: `${debtPct}%` }}>
                  {debtPct.toFixed(0)}%
                </div>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Capital propio</span>
                <span>Credito bancario ({fin.cost_of_debt}% interes anual)</span>
              </div>
              {debtPct > 60 && (
                <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/20">
                  <p className="text-xs text-amber-400 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Atencion: Tu nivel de deuda es alto. Si las ventas bajan, los pagos del credito seguiran iguales. Intenta no exceder el 50-60% de deuda.
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Financias todo con tus propios recursos. Eso significa que no tendras pagos de credito, pero todo el riesgo es tuyo.
            </p>
          )}
          <Metric label="Costo promedio de tu dinero (WACC)" value={`${wacc.toFixed(1)}%`} hint="Es el rendimiento minimo que necesitas para que valga la pena" />
        </div>
      </Section>

      {/* Section 3: Revenue & Profit */}
      <Section icon={TrendingUp} title="¿Cuanto vas a ganar?" subtitle={`${fc(avgNetIncomeMonthly)}/mes promedio`} signal={profitSignal}>
        <div className="space-y-3 mt-3">
          <p className="text-sm text-muted-foreground">
            En {years} años, tu proyecto generaria <strong className="text-foreground">{fc(totalRevenue)}</strong> en ventas totales.
            Despues de pagar costos, gastos, intereses e impuestos, te quedarian <strong className={totalNetIncome > 0 ? 'text-emerald-400' : 'text-red-400'}>{fc(totalNetIncome)}</strong> de utilidad neta.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-secondary/30 text-center">
              <p className="text-xs text-muted-foreground mb-1">Ventas Año 1</p>
              <p className="font-bold text-sm">{fc(year1Revenue)}</p>
            </div>
            <div className="p-3 rounded-lg bg-secondary/30 text-center">
              <p className="text-xs text-muted-foreground mb-1">Ventas Año {years}</p>
              <p className="font-bold text-sm">{fc(lastYearRevenue)}</p>
            </div>
            <div className="p-3 rounded-lg bg-secondary/30 text-center">
              <p className="text-xs text-muted-foreground mb-1">Crecimiento</p>
              <p className="font-bold text-sm flex items-center justify-center gap-1">
                {revenueGrowth > 0 ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> : <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
                {revenueGrowth.toFixed(0)}%
              </p>
            </div>
          </div>
          <Metric label="Utilidad neta mensual promedio" value={fc(avgNetIncomeMonthly)} hint="Lo que le quedaria al negocio cada mes en promedio" />
          {totalNetIncome <= 0 && (
            <div className="p-3 rounded-lg bg-red-400/10 border border-red-400/20">
              <p className="text-xs text-red-400 flex items-center gap-1.5">
                <XCircle className="w-3.5 h-3.5" />
                Tu proyecto opera con perdidas. Revisa si puedes aumentar precios, reducir costos o incrementar volumen.
              </p>
            </div>
          )}
        </div>
      </Section>

      {/* Section 4: Payback */}
      <Section icon={Clock} title="¿Cuando recuperas tu dinero?" subtitle={payback ? `${payback.toFixed(1)} años` : 'No se recupera'} signal={paybackSignal}>
        <div className="space-y-3 mt-3">
          {payback ? (
            <>
              <p className="text-sm text-muted-foreground">
                Si todo sale como esta planeado, recuperarias tu inversion de {fc(totalInv)} en aproximadamente <strong className="text-foreground">{payback.toFixed(1)} años</strong>
                {payback < 2 ? ' — eso es bastante rapido.' : payback < 4 ? ' — un tiempo razonable.' : ' — considera si puedes acelerar las ventas.'}
              </p>
              {/* Visual progress bar */}
              <div className="space-y-1">
                <div className="h-3 rounded-full bg-secondary/50 overflow-hidden">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(100, (payback / years) * 100)}%` }} />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Hoy</span>
                  <span className="text-primary font-medium">Recuperacion: Año {payback.toFixed(1)}</span>
                  <span>Año {years}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Despues del año {Math.ceil(payback)}, todo lo que genere el proyecto ya es ganancia neta para ti.
              </p>
            </>
          ) : (
            <div className="p-3 rounded-lg bg-red-400/10 border border-red-400/20">
              <p className="text-xs text-red-400 flex items-center gap-1.5">
                <XCircle className="w-3.5 h-3.5" />
                Con las proyecciones actuales, no recuperarias tu inversion en {years} años. Necesitas ajustar ingresos o costos.
              </p>
            </div>
          )}
        </div>
      </Section>

      {/* Section 5: Is it worth it? */}
      <Section icon={Scale} title="¿Vale la pena invertir?" subtitle={isViable ? 'Si, los indicadores son positivos' : 'Los indicadores sugieren precaucion'} signal={viabilitySignal}>
        <div className="space-y-3 mt-3">
          <p className="text-sm text-muted-foreground">
            Aqui la pregunta clave: ¿tu proyecto te da mas dinero del que obtendrias dejandolo en el banco o invirtiendo en otra cosa?
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className={`p-4 rounded-lg border ${npv > 0 ? 'border-emerald-400/30 bg-emerald-400/5' : 'border-red-400/30 bg-red-400/5'}`}>
              <p className="text-xs text-muted-foreground mb-1">Valor que genera (VAN)</p>
              <p className={`text-lg font-bold ${npv > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fc(npv)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {npv > 0
                  ? 'Tu proyecto genera riqueza adicional a lo invertido'
                  : 'El proyecto destruye valor — pierdes dinero'
                }
              </p>
            </div>
            <div className={`p-4 rounded-lg border ${irr > wacc ? 'border-emerald-400/30 bg-emerald-400/5' : 'border-red-400/30 bg-red-400/5'}`}>
              <p className="text-xs text-muted-foreground mb-1">Rendimiento del proyecto (TIR)</p>
              <p className={`text-lg font-bold ${irr > wacc ? 'text-emerald-400' : 'text-red-400'}`}>{irr.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground mt-1">
                {irr > wacc
                  ? `Supera el minimo requerido (${wacc.toFixed(1)}%)`
                  : `No alcanza el minimo requerido (${wacc.toFixed(1)}%)`
                }
              </p>
            </div>
          </div>
          <div className="p-3 rounded-lg bg-secondary/30">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">¿Que significa?</strong> El VAN te dice cuanto dinero "extra" generas (en pesos de hoy). La TIR es como una tasa de interes: si es mayor que lo que te cuesta el dinero ({wacc.toFixed(1)}%), vale la pena.
            </p>
          </div>
        </div>
      </Section>

      {/* Section 6: Breakeven */}
      <Section icon={Target} title="¿Cuanto necesitas vender para no perder?" subtitle={`${beUnits.toLocaleString('es-MX', { maximumFractionDigits: 0 })} unidades/año`} signal={beSignal}>
        <div className="space-y-3 mt-3">
          <p className="text-sm text-muted-foreground">
            Tu punto de equilibrio es <strong className="text-foreground">{beUnits.toLocaleString('es-MX', { maximumFractionDigits: 0 })}</strong> unidades al año. Si vendes menos, pierdes dinero. Si vendes mas, ganas.
          </p>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="h-3 rounded-full bg-secondary/50 overflow-hidden relative">
                <div className="h-full rounded-full bg-amber-500 absolute" style={{ width: `${Math.min(100, projectedUnits > 0 ? (beUnits / projectedUnits * 100) : 0)}%` }} />
                <div className="h-full rounded-full bg-emerald-500/30 absolute" style={{ width: '100%' }} />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0</span>
                <span className="text-amber-400">Minimo: {beUnits.toLocaleString('es-MX', { maximumFractionDigits: 0 })}</span>
                <span className="text-emerald-400">Proyeccion: {projectedUnits.toLocaleString('es-MX', { maximumFractionDigits: 0 })}</span>
              </div>
            </div>
          </div>
          <Metric
            label="Margen de seguridad"
            value={`${marginSafety.toFixed(0)}%`}
            hint={marginSafety > 30 ? 'Buen colchon — tus ventas pueden caer y seguirias siendo rentable' : marginSafety > 10 ? 'Aceptable, pero no hay mucho margen de error' : 'Peligroso — cualquier baja en ventas te pone en perdida'}
          />
        </div>
      </Section>

      {/* Section 7: Scenarios */}
      <Section icon={Shield} title="¿Que pasa si las cosas no salen perfectas?" subtitle={pessDecision === 'ACEPTADO' ? 'Incluso con problemas, aguanta' : 'Si bajan las ventas, hay riesgo'} signal={scenarioSignal}>
        <div className="space-y-3 mt-3">
          <p className="text-sm text-muted-foreground">
            Simulamos tres escenarios: uno donde todo sale mejor de lo esperado, el plan base, y uno donde las ventas caen y los costos suben.
          </p>
          <div className="space-y-2">
            {['optimistic', 'base', 'pessimistic'].map(key => {
              const s = scenarios[key];
              if (!s) return null;
              const labels = { optimistic: 'Mejor caso', base: 'Plan base', pessimistic: 'Peor caso' };
              const icons = { optimistic: TrendingUp, base: ArrowRight, pessimistic: TrendingDown };
              const colors = { optimistic: 'text-emerald-400', base: 'text-primary', pessimistic: 'text-amber-400' };
              const Icon = icons[key];
              return (
                <div key={key} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                  <Icon className={`w-4 h-4 ${colors[key]} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{labels[key]}</span>
                    <span className="text-xs text-muted-foreground ml-2">{s.label}</span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`text-sm font-bold ${s.decision === 'ACEPTADO' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {s.decision === 'ACEPTADO' ? 'Viable' : 'No viable'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          {pessDecision !== 'ACEPTADO' && (
            <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/20">
              <p className="text-xs text-amber-400 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                En el peor escenario tu proyecto no es viable. Esto no significa que fallaras, pero conviene tener un plan B o un fondo de emergencia.
              </p>
            </div>
          )}
        </div>
      </Section>

      {/* Section 8: Perpetuity */}
      {perpEnabled && perpFormula && (
        <Section icon={Infinity} title="¿Que valor tiene tu negocio a perpetuidad?" subtitle={fc(terminalValue)} signal="green" defaultOpen={true}>
          <div className="space-y-3 mt-3">
            <p className="text-sm text-muted-foreground">
              Ademas del valor que genera en {years} años, tu negocio tiene un <strong className="text-foreground">valor terminal</strong> — es lo que valdria si sigue operando indefinidamente. Piensalo como el precio al que podrias vender el negocio al final del horizonte de proyeccion.
            </p>
            <div className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Flujo del ultimo año</span>
                  <span className="font-semibold tabular-nums">{fc(perpFormula.last_year_fcf)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">(-) Reinversion para mantener el negocio</span>
                  <span className="font-semibold tabular-nums text-red-400">- {fc(perpFormula.capex_replacement)}</span>
                </div>
                <div className="flex justify-between border-t border-violet-500/20 pt-2">
                  <span className="font-medium">Flujo disponible a perpetuidad</span>
                  <span className="font-bold tabular-nums">{fc(perpFormula.adjusted_fcf)}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground pt-1">
                  <span>Crecimiento anual: {perpFormula.growth_rate}% | WACC: {perpFormula.denominator_wacc}%</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-violet-500/20 flex justify-between items-center">
                <span className="font-medium text-sm">Valor Terminal del Negocio</span>
                <span className="text-xl font-bold text-violet-400 tabular-nums">{fc(terminalValue)}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Esto significa que, ademas de las ganancias de los {years} años proyectados, tu negocio tiene un valor adicional de <strong className="text-violet-400">{fc(terminalValue)}</strong> por su capacidad de seguir generando dinero en el futuro.
            </p>
            {perpData.has_salvage_warning && (
              <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/20">
                <p className="text-xs text-amber-400 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Nota: Tienes activos con valor de rescate. Parte de ese valor podria estar contandose dos veces con la perpetuidad. Revisa con tu asesor.
                </p>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Recommendations */}
      <Card className="border-primary/30 bg-primary/5" data-testid="recommendations-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lightbulb className="w-5 h-5 text-primary" />
            Recomendaciones
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Recommendation
            text="Revisa tus costos cada 3 meses. Los precios de insumos cambian y un aumento pequeño puede afectar mucho a largo plazo."
            type="always"
          />
          {debtPct > 40 && (
            <Recommendation
              text={`Tienes ${debtPct.toFixed(0)}% de deuda. Intenta hacer pagos anticipados cuando tengas excedentes para reducir los intereses.`}
              type="warning"
            />
          )}
          {marginSafety < 25 && (
            <Recommendation
              text="Tu margen de seguridad es bajo. Considera diversificar tus fuentes de ingreso o reducir gastos fijos para tener mas colchon."
              type="warning"
            />
          )}
          {payback && payback > years * 0.5 && (
            <Recommendation
              text="Tardas mas de la mitad de la vida del proyecto en recuperar tu inversion. Busca formas de acelerar las ventas en los primeros años."
              type="info"
            />
          )}
          <Recommendation
            text="No tomes decisiones solo con estos numeros. Visita el local, habla con posibles clientes y proveedores, y valida tus supuestos antes de invertir."
            type="always"
          />
          <Recommendation
            text="Guarda un fondo de emergencia de al menos 3 meses de gastos fijos antes de arrancar. Lo inesperado siempre llega."
            type="always"
          />
          {revenueGrowth > 50 && (
            <Recommendation
              text={`Proyectas un crecimiento de ${revenueGrowth.toFixed(0)}% en ventas. Asegurate de que sea realista y que tengas la capacidad operativa para atender esa demanda.`}
              type="info"
            />
          )}
        </CardContent>
      </Card>

      {/* Footer note */}
      <p className="text-xs text-muted-foreground text-center pb-6">
        Este resumen es una guia orientativa basada en los datos que ingresaste. Consulta a un asesor financiero para decisiones definitivas.
      </p>
    </div>
  );
};

const Recommendation = ({ text, type }) => {
  const icons = { always: Lightbulb, warning: AlertTriangle, info: BarChart3 };
  const colors = { always: 'text-primary', warning: 'text-amber-400', info: 'text-blue-400' };
  const Icon = icons[type] || Lightbulb;
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colors[type] || 'text-primary'}`} />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
};
